/**
 * FooterSection - Simple footer with nav links, logo, and copyright
 */
import Link from 'next/link'
import {
  cn,
  LogoMasterClassWordmark,
  MarkPrivacyOptions,
} from '@mc/educator-design-system'
import { isGeo } from '@mc/geo'
import { useTranslation } from '@mc/i18n'
import { TrackClick } from '../../tracking/educatorTracking'

/** Footer navigation links */
const FOOTER_NAV = {
  explore: {
    links: [
      { id: 'masterclass', href: 'https://www.masterclass.com' },
      { id: 'certificates', href: 'https://www.masterclass.com/certificates' },
      { id: 'onCall', href: 'https://oncall.masterclass.com' },
    ],
  },
  about: {
    links: [
      { id: 'careers', href: 'https://careers.masterclass.com/' },
      { id: 'privacy', href: 'https://privacy.masterclass.com/' },
      { id: 'terms', href: 'https://www.masterclass.com/terms' },
      {
        id: 'learnerGuidelines',
        href: 'https://www.masterclass.com/terms/MCX-Learner-Guidelines',
      },
    ],
  },
} as const

interface FooterSectionProps {
  linkLocation?: 'tos' | 'footer'
  /** Pre-paywall pages still sit on the 20px phone margin, so their footer
   *  has to match it rather than the post-paywall page-margin token. */
  margin?: 'page' | 'prePaywall'
}

export const FooterSection = ({
  linkLocation = 'tos',
  margin = 'page',
}: FooterSectionProps) => {
  const { t } = useTranslation('@mc/educator')
  const copyrightYear = new Date().getFullYear()

  return (
    <footer
      data-section='footer'
      className={cn(
        'relative z-[1] flex flex-col items-center gap-5 py-8',
        margin === 'page' ? 'px-page-margin' : 'px-5 md:px-16',
      )}
      style={{ backgroundColor: '#1a1a1a' }}
    >
      {/* Navigation Links */}
      <div className='flex w-full gap-10 pb-5 text-body-4'>
        {/* Explore Column */}
        <div className='flex flex-col gap-2'>
          <span style={{ color: 'rgba(255, 255, 255, 0.3)' }}>
            {t('enrollment.footer.explore.title')}
          </span>
          {FOOTER_NAV.explore.links.map((link) => (
            <TrackClick
              key={link.id}
              event='educatorSignClick'
              action={link.id}
              location={linkLocation}
            >
              <Link
                href={link.href}
                className='text-white'
                style={{ textDecoration: 'underline' }}
              >
                {t(`enrollment.footer.explore.${link.id}`)}
              </Link>
            </TrackClick>
          ))}
        </div>

        {/* About Column */}
        <div className='flex flex-col gap-2'>
          <span style={{ color: 'rgba(255, 255, 255, 0.3)' }}>
            {t('enrollment.footer.about.title')}
          </span>
          {FOOTER_NAV.about.links.map((link) => (
            <TrackClick
              key={link.id}
              event='educatorSignClick'
              action={link.id}
              location={linkLocation}
            >
              <Link
                href={link.href}
                className='text-white'
                style={{ textDecoration: 'underline' }}
              >
                {t(`enrollment.footer.about.${link.id}`)}
              </Link>
            </TrackClick>
          ))}
          {isGeo('US') && (
            <>
              <TrackClick
                event='educatorSignClick'
                action='caPrivacy'
                location={linkLocation}
              >
                <Link
                  href='https://privacy.masterclass.com/policies?name=california-privacy-notice'
                  className='text-white'
                  style={{ textDecoration: 'underline' }}
                >
                  {t('enrollment.footer.about.caPrivacy')}
                </Link>
              </TrackClick>
              <TrackClick
                event='educatorSignClick'
                action='doNotSell'
                location={linkLocation}
              >
                <Link
                  href='https://privacy.masterclass.com/policies?modal=select-subject'
                  className='flex items-center gap-1 text-white'
                  style={{ textDecoration: 'underline' }}
                >
                  {t('enrollment.footer.about.doNotSell')}
                  <MarkPrivacyOptions className='shrink-0' />
                </Link>
              </TrackClick>
            </>
          )}
        </div>
      </div>

      {/* Logo and Copyright Row */}
      <div className='flex w-full flex-col gap-4 md:flex-row md:items-center'>
        <LogoMasterClassWordmark className='h-[25px] w-[111px] text-white' />
        <div className='hidden flex-1 md:block' />
        <div className='flex gap-[10px] text-body-4 text-white'>
          <span>
            {t('enrollment.footer.copyright', { year: copyrightYear })}
          </span>
          <span>{t('enrollment.footer.secured')}</span>
        </div>
      </div>
    </footer>
  )
}
